import React, { useContext } from "react";
import PropTypes from "prop-types";
import MaskedInput from "react-text-mask";
import NumberFormat from "react-number-format";
import clsx from "clsx";
import AppContext from "AppContext";
import currencies from "utils/static/currencies";

export const masks = {
    ssn: {
        mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
        char: "_",
    },
    alienRegistrationNo: {
        mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/],
        char: "_",
    },
    phoneNumber: {
        mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
        char: "_",
    },
    twoDigits: {
        mask: [/\d/, /\d/],
        char: "_",
    },
};

function InputWrapper({ children, name, label, error, helperText, dark, light, compact, capitalize, uppercase, lowercase }) {
    const hasError = Boolean(error);
    const hText = error || helperText;

    return (
        <div
            className={clsx("form-item", dark ? "form-item--dark" : "form-item", {
                "form-item--capitalize": capitalize,
                "form-item--uppercase": uppercase,
                "form-item--lowercase": lowercase,
            })}
        >
            {label && <label htmlFor={name}>{label}</label>}
            {children}
            {hText && <p className={clsx({ error: hasError, helperText: !hasError })}>{hText}</p>}
        </div>
    );
}

function Input(props) {
    const { mask, currency, numeric, label, error, helperText, dark, light, compact, capitalize, uppercase, lowercase, ...other } = props;
    const { configurations } = useContext(AppContext);
    const currencyConfig = configurations.find((config) => config.id === "general_currency");
    const currencyIsoCode = currencyConfig?.value;
    const currencyObj = currencies.find((c) => c.isoCode === currencyIsoCode);
    const currencySymbol = currency ? `${currencyObj?.symbol || "$"} ` : "";

    if (mask) {
        return (
            <InputWrapper
                name={other.name}
                label={label}
                error={error}
                helperText={helperText}
                dark={dark}
                light={light}
                compact={compact}
                capitalize={capitalize}
                uppercase={uppercase}
                lowercase={lowercase}
            >
                <MaskedInput {...other} mask={mask.mask} placeholderChar={mask.char} />
            </InputWrapper>
        );
    }

    if (currency || numeric) {
        const { onChange } = other;
        return (
            <InputWrapper
                name={other.name}
                label={label}
                error={error}
                helperText={helperText}
                dark={dark}
                light={light}
                compact={compact}
                capitalize={capitalize}
                uppercase={uppercase}
                lowercase={lowercase}
            >
                <NumberFormat
                    {...other}
                    onValueChange={(values) => {
                        onChange({
                            target: {
                                name: props.name,
                                value: values.value,
                            },
                        });
                    }}
                    thousandSeparator
                    isNumericString
                    prefix={currencySymbol}
                />
            </InputWrapper>
        );
    }

    return (
        <InputWrapper
            name={other.name}
            label={label}
            error={error}
            helperText={helperText}
            dark={dark}
            light={light}
            compact={compact}
            capitalize={capitalize}
            uppercase={uppercase}
            lowercase={lowercase}
        >
            <input aria-invalid={false} {...other} />
        </InputWrapper>
    );
}

Input.propTypes = {
    mask: PropTypes.oneOf([masks.ssn, masks.alienRegistrationNo, masks.phoneNumber, masks.twoDigits]),
    currency: PropTypes.bool,
    numeric: PropTypes.bool,
    onChange: PropTypes.func,
    label: PropTypes.node,
    error: PropTypes.string,
    helperText: PropTypes.string,
    dark: PropTypes.bool,
    light: PropTypes.bool,
    compact: PropTypes.bool,
    capitalize: PropTypes.bool,
    uppercase: PropTypes.bool,
    lowercase: PropTypes.bool,
};

Input.defaultProps = {
    mask: undefined,
    currency: false,
    numeric: false,
    onChange: undefined,
    label: undefined,
    error: undefined,
    helperText: undefined,
    dark: false,
    light: false,
    compact: false,
    capitalize: false,
    uppercase: false,
    lowercase: false,
};

export default Input;
